import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_BaseLnRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbBaseLn} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_BaseLn, esWB_BaseLnMt, esWB_Workflow
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {BaseLnBOValidations} from "./BaseLnBO.Validations";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {PlanTypeBOAudit} from "./PlanTypeBO.Audit";
import {AuditService} from "../PA.Core/Security/AuditService";
import {BaseLnBOAudit} from "./BaseLnBO.Audit";

export interface IBaselineLoadOptions {
    dbContext?: PAdbContext
}

const BaselineLoadOptions_default: IBaselineLoadOptions = {}

export class BaseLnBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_BaseLn: WB_BaseLnRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Baseline'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_BaseLn = undefined;
    }

    //</editor-fold>

    get WB_BaseLn(): WB_BaseLnRow {
        return this.drWB_BaseLn;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Baseline");

        let internalOnComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Baseline.";
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_BaseLn)
                .then((newBaselineRow: WB_BaseLnRow) => {
                    newBaselineRow.setDefaults()
                    self.drWB_BaseLn = newBaselineRow;
                    self.dbContext.add(esWB_BaseLn, newBaselineRow)
                    self.Identity.setAll(self.WB_BaseLn.cif.WB_BASELINE_ID, self.WB_BaseLn.cif.BASELINE_ID, self.WB_BaseLn.cif.BASELINE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_BaseLn, 'WB_BASELINE_ID', 'BASELINE_ID', 'BASELINE_NAME')


                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        self._OnLoadComplete = internalOnComplete

        return new Promise<IBusinessObject>((resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve()
                }).then((ignored) => {
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.finish()
                resolve(self)
            }).catch((err) => {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
            })
        })
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Baseline already loaded.")
                resolve(self); // "Baseline already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, BaselineLoadOptions_default)


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for Baseline.")
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = BaseLnBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = BaseLnBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function () {
                    let aBaselineRow: any = recordResolver.get(esWB_BaseLn, anId)

                    if (_.isEmpty(aBaselineRow)) {
                        error("Baseline not Found");
                        return;
                    }

                    self.drWB_BaseLn = aBaselineRow;
                    self.Identity.setAll(self.WB_BaseLn.cif.WB_BASELINE_ID, self.WB_BaseLn.cif.BASELINE_ID, self.WB_BaseLn.cif.BASELINE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_BaseLn, 'WB_BASELINE_ID', 'BASELINE_ID', 'BASELINE_NAME')
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return;
            });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_BaseLn.cif.BASELINE_NAME + "(" + self.WB_BaseLn.cif.BASELINE_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate Baseline.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            BaseLnBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        var self = this;
        var wbBaseline = <IwbBaseLn>self.drWB_BaseLn;
        this.SaveProgressProvider.start("Saving " + wbBaseline.BASELINE_NAME + "(" + wbBaseline.BASELINE_ID + ")");
        let auditService: AuditService = undefined

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == undefined) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save Baseline.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }

            self.SaveOptions = options || {};

            var saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }
            let bDone = false;

            // Add code to do individual BO save for dbContextRows....
            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return
                    }
                    //

                }).then((ignore) => {
                if (bDone) return
                return BaseLnBOAudit.BaselineAuditBO(self)

            }).then((anAuditService) => {
                if (bDone) return
                auditService = anAuditService
                return saveFunction(esWB_BaseLn, self.drWB_BaseLn, {})
            }).then((ignored) => {
                if (bDone) return
                if (auditService) {
                    return auditService.Save(bBatch)
                }
            })
                .then((ignored) => {
                    if (bDone) return
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        })
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err);
                    reject(self)
                })
        });
    }


    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbBaseline = <IwbBaseLn>self.drWB_BaseLn;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbBaseline.BASELINE_NAME + "(" + wbBaseline.WB_BASELINE_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Baseline

            // 2. If Valid, mark all the records for deletion.
            self.drWB_BaseLn.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

//</editor-fold>


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const bsln_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
            dctExcludeHasManyNamedRelations: {
                [esWB_BaseLn]: [esWB_BaseLnMt]
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, bsln_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_BaseLn, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }


};
PAEnvironment.registerBusinessObjectClass(BaseLnBO, "BaseLnBO", "BaseLn")